import { useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import {
  BillRowVm,
  BillSectionVm,
} from '@/features/dashboard/bookingChanges/types/BillViewModel.ts';
import { BillSection } from '@/features/dashboard/bookingChanges/components/primitives';
import { prepareSections } from './changePreview';

interface BillSectionListProps extends React.HTMLAttributes<HTMLDivElement> {
  sections: BillSectionVm[];
  isEditing: boolean;
  /** Filtr „Tylko zmienione" — działa wyłącznie w trybie edycji. */
  onlyChanged?: boolean;
  onRowAction?: (row: BillRowVm) => void;
  onAddManualRow?: (section: BillSectionVm) => void;
}

/**
 * Kolumna sekcji — wspólna dla ekranu uczestnika, zapisującego i pozycji całej
 * rezerwacji. Trzyma stan zwinięcia (sekcje domyślnie rozwinięte) i przepuszcza
 * dane przez `prepareSections`, więc każdy z tych ekranów tak samo reaguje na
 * tryb podglądu i na filtr „Tylko zmienione".
 */
const BillSectionList = ({
  sections,
  isEditing,
  onlyChanged = false,
  onRowAction,
  onAddManualRow,
  className,
  ...props
}: BillSectionListProps) => {
  const [closedSectionIds, setClosedSectionIds] = useState<
    Record<string, boolean>
  >({});

  const visibleSections = useMemo(
    () => prepareSections(sections, { isEditing, onlyChanged }),
    [sections, isEditing, onlyChanged],
  );

  const toggleSection = (sectionId: string) =>
    setClosedSectionIds((closed) => ({
      ...closed,
      [sectionId]: !closed[sectionId],
    }));

  return (
    <div className={cn('flex flex-col gap-3.5', className)} {...props}>
      {visibleSections.map((section) => (
        <BillSection
          key={section.id}
          section={section}
          isEditing={isEditing}
          isOpen={!closedSectionIds[section.id]}
          onToggle={() => toggleSection(section.id)}
          onRowAction={onRowAction}
          onAddManualRow={() => onAddManualRow?.(section)}
        />
      ))}
    </div>
  );
};

export default BillSectionList;
